import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Division,
  EmptyState,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Select,
  Skeleton,
  Spin,
  Table,
  useToast,
  type SelectOption,
  type TableColumnType,
} from "@ir/ui";
import {
  CreateMasterDataProductPriceDTO,
  GetMasterDataProductPriceHistoryDTO,
} from "@src/domain/dto/masterData";
import {
  MasterDataProductPriceHistory,
  type IMasterDataProductPriceHistoryItem,
  type MasterDataProduct,
} from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataProductPriceUseCase,
  GetMasterDataProductPriceHistoryUseCase,
  GetMasterDataPurchasersUseCase,
} from "@src/application/useCases/masterData";
import {
  MasterDataProductPriceRepository,
  MasterDataPurchaserRepository,
} from "@src/infrastructure/repositories/masterData";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import {
  loadMasterDataPurchasersLookup,
  type MasterDataPurchaserLookupOption,
} from "@src/presentation/providers/masterData/loadMasterDataPurchasersLookup";
import {
  getMasterDataProductPriceHistoryCache,
  getMasterDataProductPriceHistoryEpoch,
  getMasterDataPurchasersLookupCache,
  getMasterDataPurchasersLookupEpoch,
  invalidateMasterDataListCacheScopes,
  loadMasterDataProductPriceHistory,
  masterDataProductPriceHistoryCacheKey,
} from "@src/presentation/providers/masterData/masterDataListCache";
import { useMasterDataBrand } from "../MasterDataBrandContext";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  renderMasterDataTextCell,
  sumMasterDataColumnWidths,
} from "./masterDataTable.shared";
import { MasterDataProductPriceAddForm } from "./MasterDataProductPriceAddForm";
import { getMasterDataSaveErrorMessage } from "./masterDataSaveErrors";
import {
  ALL_PURCHASERS_SCOPE,
  EMPTY_PRODUCT_PRICE_ADD_VALUES,
  SPECIFIC_PURCHASER_SCOPE,
  productPriceAppliesToLabel,
  priceHistoryStartsOn,
  productPriceMinDate,
  productPriceNextDate,
  productPriceScopeKey,
  type ProductPriceAddFormValues,
  type ProductPricePurchaserOption,
} from "./productPriceAddForm.shared";

const PERIOD_OPTIONS: SelectOption[] = [
  { value: "6", label: "Last 6 months" },
  { value: "12", label: "Last 12 months" },
  { value: "18", label: "Last 18 months" },
];

interface HistoryRow extends IMasterDataProductPriceHistoryItem {
  key: string;
}

interface MasterDataProductPriceHistoryModalProps {
  product: MasterDataProduct | null;
  isOpen: boolean;
  onClose: () => void;
}

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatIsoDate(value: string | null | undefined): string {
  if (!value) return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(
    Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])),
  );
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

function formatPeriodLabel(start: string | null, end: string | null): string {
  if (!start && !end) return "";
  return `${formatIsoDate(start)} – ${formatIsoDate(end)}`;
}

const API_FIELD_BY_KEY: Record<string, keyof ProductPriceAddFormValues> = {
  product_price: "productPrice",
  effective_date: "startDate",
  notes: "notes",
  purchaser_id: "purchaserId",
};

function fieldFromApiMessage(message: string): {
  field?: keyof ProductPriceAddFormValues;
  text: string;
} {
  const separator = message.indexOf("::");
  if (separator < 0) return { text: message };
  const key = message.slice(0, separator).trim();
  const text = message.slice(separator + 2).trim();
  return { field: API_FIELD_BY_KEY[key], text };
}

function cellDateText(value: unknown): string | null {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "bigint")
    return String(value);
  return null;
}

function cellMoney(value: unknown): number | null {
  if (typeof value === "number") return Number.isFinite(value) ? value : null;
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

type PriceHistoryPeriod = "6" | "12" | "18";

const EMPTY_PURCHASERS: ProductPricePurchaserOption[] = [];

function readCachedPriceHistory({
  isOpen,
  brandKey,
  productId,
  purchaserScope,
  period,
  loadedHistory,
  loadedHistoryKey,
  fetchError,
  fetchErrorKey,
}: {
  isOpen: boolean;
  brandKey: string | null;
  productId: number | null;
  purchaserScope: string;
  period: string;
  loadedHistory: MasterDataProductPriceHistory | null;
  loadedHistoryKey: string | null;
  fetchError: string | null;
  fetchErrorKey: string | null;
}): {
  historyCacheKey: string | null;
  history: MasterDataProductPriceHistory | null;
  error: string | null;
  loading: boolean;
} {
  const historyCacheKey =
    isOpen && productId != null && brandKey
      ? masterDataProductPriceHistoryCacheKey(
          brandKey,
          productId,
          purchaserScope,
          period,
        )
      : null;
  const cachedHistory = historyCacheKey
    ? getMasterDataProductPriceHistoryCache(historyCacheKey)
    : undefined;
  const history =
    cachedHistory ??
    (loadedHistoryKey === historyCacheKey ? loadedHistory : null);
  const error = fetchErrorKey === historyCacheKey ? fetchError : null;
  return {
    historyCacheKey,
    history,
    error,
    loading:
      isOpen &&
      productId != null &&
      brandKey != null &&
      history == null &&
      error == null,
  };
}

function useAddPriceEffectiveMinDate({
  brandKey,
  productId,
  addOpen,
  targetScope,
  period,
  historyUseCase,
}: {
  brandKey: string | null;
  productId: number | null;
  addOpen: boolean;
  targetScope: string | null;
  period: PriceHistoryPeriod;
  historyUseCase: GetMasterDataProductPriceHistoryUseCase;
}) {
  const today = productPriceMinDate();
  const cacheKey =
    brandKey && productId != null && targetScope != null
      ? masterDataProductPriceHistoryCacheKey(
          brandKey,
          productId,
          targetScope,
          period,
        )
      : null;
  const cachedItems = cacheKey
    ? getMasterDataProductPriceHistoryCache(cacheKey)?.items
    : undefined;
  const [loadedItems, setLoadedItems] = useState<
    IMasterDataProductPriceHistoryItem[] | null
  >(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);

  useEffect(() => {
    if (!addOpen || cacheKey == null || productId == null || targetScope == null) {
      return;
    }
    if (getMasterDataProductPriceHistoryCache(cacheKey)) return;

    let cancelled = false;
    const epoch = getMasterDataProductPriceHistoryEpoch();
    const scope = targetScope;
    void loadMasterDataProductPriceHistory(cacheKey, () =>
      historyUseCase.execute(
        new GetMasterDataProductPriceHistoryDTO({
          productId,
          purchaserId: scope === ALL_PURCHASERS_SCOPE ? null : Number(scope),
          period,
        }),
      ),
    )
      .then((result) => {
        if (cancelled || epoch !== getMasterDataProductPriceHistoryEpoch()) return;
        setLoadedItems(result.items);
        setLoadedKey(cacheKey);
      })
      .catch(() => {
        if (cancelled || epoch !== getMasterDataProductPriceHistoryEpoch()) return;
        setLoadedItems([]);
        setLoadedKey(cacheKey);
      });
    return () => {
      cancelled = true;
    };
  }, [addOpen, cacheKey, historyUseCase, period, productId, targetScope]);

  const targetItems =
    cachedItems ?? (loadedKey === cacheKey ? loadedItems : null);
  const scopeItems =
    targetScope === ALL_PURCHASERS_SCOPE
      ? targetItems?.filter((item) => item.purchaserId == null)
      : targetItems;
  const todayTaken =
    scopeItems != null && priceHistoryStartsOn(scopeItems, today);

  return {
    minDate: todayTaken ? productPriceNextDate(today) : today,
  };
}

function readSelectValue(next: unknown, fallback: string): string {
  if (next && typeof next === "object" && "value" in next) {
    return String((next as SelectOption).value);
  }
  return fallback;
}

function priceHistoryLabels(
  historyProduct: MasterDataProductPriceHistory["product"] | undefined,
  fallback: MasterDataProduct | null,
) {
  return {
    productName: historyProduct?.itemName || fallback?.itemName || "Product",
    productKey: historyProduct?.productKey || fallback?.productKey || "—",
    categoryName:
      historyProduct?.categoryName || fallback?.categoryName || "—",
    upc: historyProduct?.upcItemNumber || fallback?.upcItemNumber || "—",
  };
}

function priceTodayDetail(
  summary: MasterDataProductPriceHistory["summary"] | undefined,
): string {
  return [
    summary?.appliesTo,
    summary?.inheritedFromBase ? "Inherited from base" : null,
  ]
    .filter((part): part is string => Boolean(part))
    .join(" · ");
}

interface PriceHistoryToolbarProps {
  purchaserScope: string;
  purchasersLoading: boolean;
  purchaserFilterOptions: SelectOption[];
  selectedPurchaser?: SelectOption;
  period: string;
  selectedPeriod?: SelectOption;
  historyLoading: boolean;
  periodLabel: string;
  onPurchaserScopeChange: (value: string) => void;
  onPeriodChange: (value: string) => void;
}

function PriceHistoryToolbar({
  purchaserScope,
  purchasersLoading,
  purchaserFilterOptions,
  selectedPurchaser,
  period,
  selectedPeriod,
  historyLoading,
  periodLabel,
  onPurchaserScopeChange,
  onPeriodChange,
}: Readonly<PriceHistoryToolbarProps>) {
  return (
    <Division className="master-data-price-history-toolbar">
      <Flex
        direction="column"
        gap={0.25}
        className="master-data-price-history-filter"
      >
        <Paragraph className="m-0 text-grey-600">Prices for</Paragraph>
        <Select
          key={`price-scope-${purchaserScope}`}
          size="medium"
          isSearchable
          isClearable={false}
          hideSelectedOptions
          isLoading={purchasersLoading}
          options={purchaserFilterOptions}
          defaultValue={selectedPurchaser}
          onChange={(next) => {
            onPurchaserScopeChange(
              readSelectValue(next, ALL_PURCHASERS_SCOPE),
            );
          }}
          aria-label="Prices for"
        />
      </Flex>
      <Flex
        direction="column"
        gap={0.25}
        className="master-data-price-history-filter"
      >
        <Paragraph className="m-0 text-grey-600">Period</Paragraph>
        <Select
          key={`price-period-${period}`}
          size="medium"
          isClearable={false}
          options={PERIOD_OPTIONS}
          defaultValue={selectedPeriod}
          onChange={(next) => {
            onPeriodChange(readSelectValue(next, "18"));
          }}
          aria-label="Period"
        />
      </Flex>
      <Flex
        direction="column"
        justifyContent="end"
        gap={0.25}
        className="master-data-price-history-range"
      >
        <Paragraph className="m-0 text-grey-600">Date range</Paragraph>
        <Paragraph className="master-data-price-history-range__value m-0 text-grey-700">
          {historyLoading ? (
            <Skeleton
              height={20}
              width={180}
              animation="wave"
              variant="rounded"
            />
          ) : (
            periodLabel || "—"
          )}
        </Paragraph>
      </Flex>
    </Division>
  );
}

function PriceTodayCard({
  loading,
  amount,
  meta,
}: Readonly<{
  loading: boolean;
  amount: number | null | undefined;
  meta: string;
}>) {
  if (loading) {
    return (
      <Card
        variant="outlined"
        padding="medium"
        className="master-data-price-history-metric"
      >
        <Paragraph className="m-0 text-grey-600">Price today</Paragraph>
        <Flex direction="column" gap={0.5} className="mt-1">
          <Skeleton height={28} width={140} animation="wave" variant="rounded" />
          <Skeleton height={14} width={160} animation="wave" variant="rounded" />
        </Flex>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      padding="medium"
      className="master-data-price-history-metric"
    >
      <Paragraph className="m-0 text-grey-600">Price today</Paragraph>
      <Heading level={4} className="m-0">
        {formatMoney(amount)}
      </Heading>
      {meta ? (
        <Paragraph className="m-0 text-grey-600">{meta}</Paragraph>
      ) : null}
    </Card>
  );
}

function EffectiveSinceCard({
  loading,
  effectiveFrom,
}: Readonly<{
  loading: boolean;
  effectiveFrom: string | null | undefined;
}>) {
  return (
    <Card
      variant="outlined"
      padding="medium"
      className="master-data-price-history-metric"
    >
      <Paragraph className="m-0 text-grey-600">Effective since</Paragraph>
      {loading ? (
        <Flex direction="column" className="mt-1">
          <Skeleton height={28} width={140} animation="wave" variant="rounded" />
        </Flex>
      ) : (
        <Heading level={4} className="m-0">
          {formatIsoDate(effectiveFrom)}
        </Heading>
      )}
    </Card>
  );
}

function NextScheduledCard({
  loading,
  price,
  date,
}: Readonly<{
  loading: boolean;
  price: number | null | undefined;
  date: string | null | undefined;
}>) {
  if (loading) {
    return (
      <Card
        variant="outlined"
        padding="medium"
        className="master-data-price-history-metric"
      >
        <Paragraph className="m-0 text-grey-600">Next scheduled</Paragraph>
        <Flex direction="column" gap={0.5} className="mt-1">
          <Skeleton height={28} width={140} animation="wave" variant="rounded" />
          <Skeleton height={14} width={160} animation="wave" variant="rounded" />
        </Flex>
      </Card>
    );
  }

  if (price == null) {
    return (
      <Card
        variant="outlined"
        padding="medium"
        className="master-data-price-history-metric"
      >
        <Paragraph className="m-0 text-grey-600">Next scheduled</Paragraph>
        <Heading level={4} className="m-0">
          —
        </Heading>
      </Card>
    );
  }

  return (
    <Card
      variant="outlined"
      padding="medium"
      className="master-data-price-history-metric"
    >
      <Paragraph className="m-0 text-grey-600">Next scheduled</Paragraph>
      <Heading level={4} className="m-0">
        {formatMoney(price)}
      </Heading>
      <Paragraph className="m-0 text-grey-700">{formatIsoDate(date)}</Paragraph>
    </Card>
  );
}

interface PriceHistoryMetricsProps {
  historyLoading: boolean;
  productPrice: number | null | undefined;
  priceTodayMeta: string;
  effectiveFrom: string | null | undefined;
  nextScheduledPrice: number | null | undefined;
  nextScheduledDate: string | null | undefined;
}

function PriceHistoryMetrics({
  historyLoading,
  productPrice,
  priceTodayMeta,
  effectiveFrom,
  nextScheduledPrice,
  nextScheduledDate,
}: Readonly<PriceHistoryMetricsProps>) {
  return (
    <Division className="master-data-price-history-metrics">
      <PriceTodayCard
        loading={historyLoading}
        amount={productPrice}
        meta={priceTodayMeta}
      />
      <EffectiveSinceCard
        loading={historyLoading}
        effectiveFrom={effectiveFrom}
      />
      <NextScheduledCard
        loading={historyLoading}
        price={nextScheduledPrice}
        date={nextScheduledDate}
      />
    </Division>
  );
}

interface PriceHistoryListProps {
  loading: boolean;
  rows: HistoryRow[];
  columns: TableColumnType<HistoryRow>[];
  tableWidth: number;
}

function PriceHistoryList({
  loading,
  rows,
  columns,
  tableWidth,
}: Readonly<PriceHistoryListProps>) {
  if (loading) {
    return (
      <Flex
        align="center"
        justifyContent="center"
        grow
        className="py-6"
        style={{ flex: 1, minHeight: 0 }}
      >
        <Spin tip="Loading..." spinning />
      </Flex>
    );
  }

  if (rows.length === 0) {
    return (
      <EmptyState
        className="bg-white border-1 border-color-grey-25 px-4 py-5"
        icon={Icons.InfoOctagon}
        title={
          <Heading level={6} className="text-grey-700 font-bold mt-3">
            No price history
          </Heading>
        }
        description="Add price to see here"
      />
    );
  }

  return (
    <Division
      style={{
        flex: 1,
        minHeight: 0,
        minWidth: 0,
        width: "100%",
        overflow: "hidden",
      }}
    >
      <Table
        columns={columns}
        dataSource={rows}
        rowHoverable
        sticky
        aria-label="Product price history"
        scroll={{ x: tableWidth, y: "100%" }}
      />
    </Division>
  );
}

export const MasterDataProductPriceHistoryModal = ({
  product,
  isOpen,
  onClose,
}: Readonly<MasterDataProductPriceHistoryModalProps>) => {
  const { brandKey } = useMasterDataBrand();
  const { success: successToast, error: errorToast } = useToast();
  const [form] = Form.useForm<ProductPriceAddFormValues>();
  const [purchaserScope, setPurchaserScope] = useState(ALL_PURCHASERS_SCOPE);
  const [period, setPeriod] = useState("18");
  const [loadedPurchasers, setLoadedPurchasers] = useState<
    ProductPricePurchaserOption[]
  >([]);
  const [loadedPurchaserEpoch, setLoadedPurchaserEpoch] = useState<
    number | null
  >(null);
  const [purchaserErrorEpoch, setPurchaserErrorEpoch] = useState<number | null>(
    null,
  );
  const [loadedHistory, setLoadedHistory] =
    useState<MasterDataProductPriceHistory | null>(null);
  const [loadedHistoryKey, setLoadedHistoryKey] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [fetchErrorKey, setFetchErrorKey] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [addTargetScope, setAddTargetScope] = useState<string | null>(
    ALL_PURCHASERS_SCOPE,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isFormSubmittable, setIsFormSubmittable] = useState(false);

  const priceRepository = useMemo(
    () => new MasterDataProductPriceRepository(),
    [],
  );
  const purchaserRepository = useMemo(
    () => new MasterDataPurchaserRepository(),
    [],
  );
  const historyUseCase = useMemo(
    () => new GetMasterDataProductPriceHistoryUseCase(priceRepository),
    [priceRepository],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataProductPriceUseCase(priceRepository),
    [priceRepository],
  );
  const purchasersUseCase = useMemo(
    () => new GetMasterDataPurchasersUseCase(purchaserRepository),
    [purchaserRepository],
  );

  const { historyCacheKey, history, error, loading } = readCachedPriceHistory({
    isOpen,
    brandKey,
    productId: product?.productId ?? null,
    purchaserScope,
    period,
    loadedHistory,
    loadedHistoryKey,
    fetchError,
    fetchErrorKey,
  });

  const purchaserEpoch = getMasterDataPurchasersLookupEpoch();
  const cachedPurchasers = brandKey
    ? getMasterDataPurchasersLookupCache<MasterDataPurchaserLookupOption>(
        brandKey,
      )
    : undefined;
  const purchasers = useMemo(
    () =>
      cachedPurchasers ??
      (loadedPurchaserEpoch === purchaserEpoch
        ? loadedPurchasers
        : EMPTY_PURCHASERS),
    [cachedPurchasers, loadedPurchaserEpoch, loadedPurchasers, purchaserEpoch],
  );
  const purchasersLoading =
    isOpen &&
    brandKey != null &&
    cachedPurchasers == null &&
    loadedPurchaserEpoch !== purchaserEpoch &&
    purchaserErrorEpoch !== purchaserEpoch;

  useEffect(() => {
    if (!isOpen || !brandKey) return;
    if (
      getMasterDataPurchasersLookupCache<MasterDataPurchaserLookupOption>(
        brandKey,
      )
    ) {
      return;
    }

    let cancelled = false;
    const epoch = getMasterDataPurchasersLookupEpoch();
    void loadMasterDataPurchasersLookup(brandKey, purchasersUseCase)
      .then((options) => {
        if (cancelled || epoch !== getMasterDataPurchasersLookupEpoch()) return;
        setLoadedPurchasers(options);
        setLoadedPurchaserEpoch(epoch);
        setPurchaserErrorEpoch(null);
      })
      .catch(() => {
        if (cancelled || epoch !== getMasterDataPurchasersLookupEpoch()) return;
        setLoadedPurchasers([]);
        setLoadedPurchaserEpoch(null);
        setPurchaserErrorEpoch(epoch);
      });
    return () => {
      cancelled = true;
    };
  }, [brandKey, isOpen, purchaserEpoch, purchasersUseCase]);

  useEffect(() => {
    if (!isOpen || product == null || historyCacheKey == null) return;
    if (getMasterDataProductPriceHistoryCache(historyCacheKey)) {
      setFetchError(null);
      setFetchErrorKey(null);
      return;
    }

    let cancelled = false;
    const epoch = getMasterDataProductPriceHistoryEpoch();
    const productId = product.productId;
    void loadMasterDataProductPriceHistory(historyCacheKey, () =>
      historyUseCase.execute(
        new GetMasterDataProductPriceHistoryDTO({
          productId,
          purchaserId:
            purchaserScope === ALL_PURCHASERS_SCOPE
              ? null
              : Number(purchaserScope),
          period: period as PriceHistoryPeriod,
        }),
      ),
    )
      .then((result) => {
        if (cancelled || epoch !== getMasterDataProductPriceHistoryEpoch()) return;
        setLoadedHistory(result);
        setLoadedHistoryKey(historyCacheKey);
        setFetchError(null);
        setFetchErrorKey(null);
      })
      .catch((err: unknown) => {
        if (cancelled || epoch !== getMasterDataProductPriceHistoryEpoch()) return;
        setLoadedHistory(null);
        setLoadedHistoryKey(null);
        setFetchError(
          err instanceof Error ? err.message : "Failed to load price history",
        );
        setFetchErrorKey(historyCacheKey);
      });
    return () => {
      cancelled = true;
    };
  }, [
    historyCacheKey,
    historyUseCase,
    isOpen,
    period,
    product,
    purchaserScope,
    reloadToken,
  ]);

  const { minDate: effectiveMinDate } = useAddPriceEffectiveMinDate({
    brandKey,
    productId: product?.productId ?? null,
    addOpen,
    targetScope: addTargetScope,
    period: period as PriceHistoryPeriod,
    historyUseCase,
  });

  const closeAll = useCallback(() => {
    setAddOpen(false);
    setAddTargetScope(ALL_PURCHASERS_SCOPE);
    setPurchaserScope(ALL_PURCHASERS_SCOPE);
    setPeriod("18");
    onClose();
  }, [onClose]);

  const handleAddTargetChange = useCallback(
    (appliesTo: string, purchaserId: number | null) => {
      setAddTargetScope(productPriceScopeKey(appliesTo, purchaserId));
    },
    [],
  );

  const purchaserFilterOptions = useMemo<SelectOption[]>(
    () => [
      { value: ALL_PURCHASERS_SCOPE, label: "All purchasers (Base list)" },
      ...purchasers.map((item) => ({
        value: String(item.purchaserId),
        label: item.purchaserName,
      })),
    ],
    [purchasers],
  );

  const columns = useMemo<TableColumnType<HistoryRow>[]>(
    () => [
      {
        title: "Start Date",
        dataIndex: "effectiveFrom",
        key: "effectiveFrom",
        width: 160,
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: (value: unknown) => formatIsoDate(cellDateText(value)),
      },
      {
        title: "End Date",
        dataIndex: "effectiveTo",
        key: "effectiveTo",
        width: 160,
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: (value: unknown) => formatIsoDate(cellDateText(value)),
      },
      {
        title: "Old Price",
        dataIndex: "oldPrice",
        key: "oldPrice",
        width: 140,
        align: "right",
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: (value: unknown) => formatMoney(cellMoney(value)),
      },
      {
        title: "New Price",
        dataIndex: "productPrice",
        key: "productPrice",
        width: 140,
        align: "right",
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: (value: unknown) => formatMoney(cellMoney(value)),
      },
      {
        title: "Applied To",
        dataIndex: "appliesTo",
        key: "appliesTo",
        width: 220,
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: (value: unknown) =>
          renderMasterDataTextCell(
            typeof value === "string"
              ? productPriceAppliesToLabel(value)
              : value,
          ),
      },
      {
        title: "Note",
        dataIndex: "notes",
        key: "notes",
        width: 360,
        verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
        cellRender: renderMasterDataTextCell,
      },
    ],
    [],
  );

  const rows = useMemo<HistoryRow[]>(
    () =>
      (history?.items ?? []).map((item) => ({
        ...item,
        key: String(item.productPriceId),
      })),
    [history],
  );
  const tableWidth = useMemo(
    () => sumMasterDataColumnWidths(columns),
    [columns],
  );

  const labels = priceHistoryLabels(history?.product, product);
  const summary = history?.summary;
  const selectedPurchaser = purchaserFilterOptions.find(
    (option) => option.value === purchaserScope,
  );
  const selectedPeriod = PERIOD_OPTIONS.find(
    (option) => option.value === period,
  );

  const handleSave = useCallback(
    async (values: ProductPriceAddFormValues) => {
      if (product == null) return;
      setIsSaving(true);
      try {
        await createUseCase.execute(
          new CreateMasterDataProductPriceDTO({
            productId: product.productId,
            purchaserId:
              values.appliesTo === SPECIFIC_PURCHASER_SCOPE
                ? Number(values.purchaserId)
                : null,
            productPrice: Number(values.productPrice),
            startDate: values.startDate,
            notes: values.notes?.trim() ? values.notes.trim() : null,
          }),
        );
        successToast({ content: "Price saved successfully" });
        invalidateMasterDataListCacheScopes([
          "product-price-history",
          "product-prices",
        ]);
        setLoadedHistory(null);
        setLoadedHistoryKey(null);
        setFetchError(null);
        setFetchErrorKey(null);
        setAddOpen(false);
        setAddTargetScope(ALL_PURCHASERS_SCOPE);
        form.resetFields();
        setReloadToken((token) => token + 1);
      } catch (err) {
        const message = getMasterDataSaveErrorMessage(
          err,
          "Failed to save product price. Please try again.",
          "Failed to save product price. Please try again later.",
        );
        const parsed = fieldFromApiMessage(message);
        if (parsed.field) {
          form.setFields([{ name: parsed.field, errors: [parsed.text] }]);
          setIsFormSubmittable(false);
        } else {
          errorToast({ content: parsed.text });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [createUseCase, errorToast, form, product, successToast],
  );

  const periodLabel = formatPeriodLabel(
    history?.periodStart ?? null,
    history?.periodEnd ?? null,
  );
  const historyLoading = loading && history == null;
  const todayDetail = priceTodayDetail(summary);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={closeAll}
        size="large"
        isFullScreen
        closable
        classNames={{ body: "master-data-price-history-modal-body" }}
        headerContent={
          <Flex
            align="center"
            justifyContent="space-between"
            gap={1}
            className="w-100 pb-2"
          >
            <Flex direction="column" gap={0.25}>
              <Heading level={6} className="text-grey-700 font-bold m-0">
                Price history — {labels.productName}
              </Heading>
              <Paragraph className="m-0 text-grey-600">
                {labels.productKey} · {labels.categoryName} · UPC {labels.upc}
              </Paragraph>
            </Flex>
            <HasAnyPermission
              requiredPermissions={[Permissions.MASTER_DATA_ADD]}
            >
              <Button
                size="small"
                variant="outline"
                onPress={() => {
                  form.setFieldsValue(EMPTY_PRODUCT_PRICE_ADD_VALUES);
                  setIsFormSubmittable(false);
                  setAddTargetScope(ALL_PURCHASERS_SCOPE);
                  setAddOpen(true);
                }}
              >
                <Icon slot="start" name={Icons.Plus} />
                Add price
              </Button>
            </HasAnyPermission>
          </Flex>
        }
        footerContent={
          <Flex
            align="center"
            justifyContent="end"
            className="w-100 m-0 py-3"
          >
            <Button size="small" color="grey" onPress={closeAll}>
              Close
            </Button>
          </Flex>
        }
      >
        {isOpen ? (
          <Flex
            direction="column"
            align="stretch"
            wrap="no-wrap"
            grow
            gap={1}
            className="master-data-price-history-modal px-2 pb-2"
          >
            <PriceHistoryToolbar
              purchaserScope={purchaserScope}
              purchasersLoading={purchasersLoading}
              purchaserFilterOptions={purchaserFilterOptions}
              selectedPurchaser={selectedPurchaser}
              period={period}
              selectedPeriod={selectedPeriod}
              historyLoading={historyLoading}
              periodLabel={periodLabel}
              onPurchaserScopeChange={setPurchaserScope}
              onPeriodChange={setPeriod}
            />
            {error ? (
              <Paragraph className="text-danger m-0">{error}</Paragraph>
            ) : (
              <>
                <PriceHistoryMetrics
                  historyLoading={historyLoading}
                  productPrice={summary?.productPrice}
                  priceTodayMeta={todayDetail}
                  effectiveFrom={summary?.effectiveFrom}
                  nextScheduledPrice={summary?.nextScheduledPrice}
                  nextScheduledDate={summary?.nextScheduledDate}
                />
                <PriceHistoryList
                  loading={historyLoading}
                  rows={rows}
                  columns={columns}
                  tableWidth={tableWidth}
                />
              </>
            )}
          </Flex>
        ) : null}
      </Modal>

      <Modal
        isOpen={addOpen}
        onClose={() => {
          if (!isSaving) {
            setAddOpen(false);
            setAddTargetScope(ALL_PURCHASERS_SCOPE);
          }
        }}
        size="medium"
        closable
        headerContent={
          <Flex direction="column" gap={0.25}>
            <Heading level={6} className="text-grey-700 font-bold m-0">
              Add price
            </Heading>
            <Paragraph className="m-0 text-grey-600">
              {labels.productName} · {labels.productKey}
            </Paragraph>
          </Flex>
        }
        footerContent={
          <Flex gap={0.5} justifyContent="end" className="w-100">
            <Button
              size="small"
              color="grey"
              onPress={() => {
                setAddOpen(false);
                setAddTargetScope(ALL_PURCHASERS_SCOPE);
              }}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="primary"
              onPress={() => form.submit()}
              isLoading={isSaving}
              isDisabled={isSaving || !isFormSubmittable}
            >
              Save price
            </Button>
          </Flex>
        }
      >
        {addOpen ? (
          <MasterDataProductPriceAddForm
            form={form}
            initialValues={EMPTY_PRODUCT_PRICE_ADD_VALUES}
            purchaserOptions={purchasers}
            purchasersLoading={purchasersLoading}
            minDate={effectiveMinDate}
            onSubmit={handleSave}
            onFormValidityChange={setIsFormSubmittable}
            onTargetChange={handleAddTargetChange}
          />
        ) : null}
      </Modal>
    </>
  );
};
